import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';

interface SocketContextValue {
    socket: Socket | null;
    isConnected: boolean;
    emit: (event: string, data: any) => void;
    on: (event: string, callback: (...args: any[]) => void) => () => void;
    joinGame: (gameId: string) => void;
}

const SocketContext = createContext<SocketContextValue | undefined>(undefined);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { token } = useAuth();
    const [socket, setSocket] = useState<Socket | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const gameIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (!token) {
            if (socket) {
                console.log('[SocketProvider] No token, disconnecting existing socket.');
                socket.disconnect();
                setSocket(null);
                setIsConnected(false);
            }
            return;
        }

        const getWSURL = () => {
            if (import.meta.env.VITE_WS_URL) return import.meta.env.VITE_WS_URL;
            if (import.meta.env.VITE_REALTIME_URL) return import.meta.env.VITE_REALTIME_URL;
            if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
                return 'http://localhost:3001';
            }
            return `https://${window.location.hostname}:3001`;
        };
        const SOCKET_URL = getWSURL();
        console.log('[SocketProvider] Connecting to:', SOCKET_URL);

        const s = io(SOCKET_URL, {
            auth: { token },
            transports: ['websocket', 'polling'],
            reconnectionAttempts: 10,
            reconnectionDelay: 1000,
        });

        s.on('connect', () => {
            setIsConnected(true);
            console.log('[SocketProvider] Connected:', s.id);
            // Re-join game if we were in one before disconnect
            if (gameIdRef.current) {
                console.log('[SocketProvider] Re-joining game after reconnect:', gameIdRef.current);
                s.emit('join_game', gameIdRef.current);
            }
        });

        s.on('disconnect', () => {
            setIsConnected(false);
            console.log('[SocketProvider] Disconnected');
        });

        setSocket(s);

        return () => {
            console.log('[SocketProvider] Cleaning up main socket.');
            s.disconnect();
            setSocket(null);
        };
    }, [token]);

    const emit = useCallback((event: string, data: any) => {
        if (socket?.connected) {
            socket.emit(event, data);
        } else {
            console.warn(`[SocketProvider] Cannot emit '${event}', socket not connected.`);
        }
    }, [socket]);

    const on = useCallback((event: string, callback: (...args: any[]) => void) => {
        if (!socket) return () => {};
        socket.on(event, callback);
        return () => {
            socket.off(event, callback);
        };
    }, [socket]);

    const joinGame = useCallback((gameId: string) => {
        gameIdRef.current = gameId;
        if (socket?.connected) {
            console.log('[SocketProvider] Emitting join_game:', gameId);
            socket.emit('join_game', gameId);
        } else {
            console.log('[SocketProvider] join_game pending socket connection:', gameId);
        }
    }, [socket]);

    return (
        <SocketContext.Provider value={{ socket, isConnected, emit, on, joinGame }}>
            {children}
        </SocketContext.Provider>
    );
};

export const useSocketContext = () => {
    const context = useContext(SocketContext);
    if (!context) {
        throw new Error('useSocketContext must be used within a SocketProvider');
    }
    return context;
};
